import React, { useCallback, useMemo, useState } from 'react';
import {
  PlainOrganizationDto,
  buildQueryFilters,
  useGetInfiniteManagedDashboardOrganizations,
} from '@sib/api';
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '@sib/api/constants';
import { useTranslation } from '@sib/i18n';
import { CrudFilter } from '@sib/types';
import { AutocompleteIndicator, ComboboxAvatarItemOption } from '@sib/ui';
import { useAutocompleteProps, useDebouncedValue } from '@sib/utils';
import { getInitials } from '../string-formatters';

const STALE_TIME_MANAGED_ORGANIZATIONS = 10 * 1000;

export interface UseManagedOrganizationsAutocompletePropsOptions {
  additionalFields?: ComboboxAvatarItemOption[];
  currentOrgData?: { id: string; name: string };
}

export const useManagedOrganizationsAutocompleteProps = ({
  additionalFields: externalFields,
  currentOrgData,
}: UseManagedOrganizationsAutocompletePropsOptions = {}) => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState('');
  const debouncedFilter = useDebouncedValue(filter, 500);

  const apiFilter = useMemo<CrudFilter[]>(
    () =>
      debouncedFilter
        ? [
            {
              field: 'name',
              value: debouncedFilter,
              operator: 'contains' as const,
            },
          ]
        : [],
    [debouncedFilter]
  );

  const payload = useMemo(
    () => ({
      page: DEFAULT_PAGE,
      size: DEFAULT_PAGE_SIZE,
      sort: ['name:asc'],
      ...(apiFilter.length && { filter: buildQueryFilters(apiFilter) }),
    }),
    [apiFilter]
  );

  const params = useGetInfiniteManagedDashboardOrganizations(payload, {
    staleTime: STALE_TIME_MANAGED_ORGANIZATIONS,
  });

  const additionalFields = useMemo<ComboboxAvatarItemOption[]>(
    () =>
      [
        currentOrgData &&
          currentOrgData.name.toLowerCase().includes(filter.toLowerCase()) && {
            label: (
              <AutocompleteIndicator indicator={t('current')}>
                {currentOrgData.name}
              </AutocompleteIndicator>
            ),
            value: currentOrgData.id,
            initial: getInitials(currentOrgData.name),
          },
        ...(externalFields?.filter(i => i.value !== currentOrgData?.id) || []),
      ].filter(Boolean) as ComboboxAvatarItemOption[],
    [externalFields, currentOrgData, filter, t]
  );

  const optionsMapper = useCallback(
    (org: PlainOrganizationDto): ComboboxAvatarItemOption => ({
      label: org.name,
      value: org.id,
      initial: getInitials(org.name),
    }),
    []
  );

  return useAutocompleteProps({
    params,
    filter,
    setFilter,
    optionsMapper,
    additionalFields,
  });
};
